import { useEffect, useState } from "react";
import { Col, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Exclamation,
  ExclamationCircleFill,
  Eye,
  EyeSlash,
} from "react-bootstrap-icons";
import { COLORS } from "utils/colors";

/**
 * InputEdit is a controlled input component with label, error display, and custom styling.
 * It supports different sizes, disabled state, and color changes on focus or error.
 * @memberof module:components/inputs
 * @param {Object} props
 * @param {string} props.label - The label text shown above the input.
 * @param {string} props.value - The current value of the input.
 * @param {string} [props.type] - The input type attribute (e.g., "text", "password"). Defaults to "text".
 * @param {boolean} [props.enabled] - If true, disables the input (inverted logic: enabled=false means editable).
 * @param {string} [props.placeholder] - Placeholder text for the input.
 * @param {string} [props.error] - Error message; when truthy, styles input with error colors and displays message.
 * @param {"sm" | "md" | "lg"} [props.size] - Size of the input and label. Default is "md".
 * @param {function(Event): void} props.onChange - Callback fired when input value changes.
 * @param {function(FocusEvent): void} [props.onFocus] - Optional callback fired when input receives focus.
 * @param {function(FocusEvent): void} [props.onBlur] - Optional callback fired when input loses focus.
 *
 * @returns {JSX.Element} The rendered input component with label and error styling.
 */
const InputEdit = ({
  label,
  disableLabel = false,
  value = "",
  type,
  enabled = true,
  disabled = false,
  placeholder,
  error,
  validationPattern,
  cleanWhitespaces = false,
  size,
  onFocus,
  onChange,
  onBlur,
}) => {
  const [isFocused, setIsFocus] = useState(false);
  const [valueEdit, setValue] = useState(value);
  const [errorEdit, setError] = useState(error);
  const [showEye, setShowEye] = useState(false); // controls visibility of the eye

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setValue(value);
  }, [value]);

  useEffect(() => {
    setError(error);
  }, [error]);

  useEffect(() => {
    if (isFocused && type === "password") setShowEye(true); // hide eye while focused

    if (!isFocused && type === "password") {setShowEye(false); setShowPassword(false)};

    
  }, [isFocused]);

  return (
    <>
      {!disableLabel && (
        <Form.Label
          column
          style={{
            // fontSize: labelFontSize,
            fontWeight: disabled ? "noarmal" : isFocused ? "bold" : "normal",
            color: disabled
              ? "#acacac"
              : isFocused
              ? COLORS.selectedColorTheme
              : error
              ? "red"
              : COLORS.greyFontColor,
          }}
        >
          {label}
        </Form.Label>
      )}
      <Col>
        <div className="position-relative">
          <Form.Control
            type={
              type === "password"
                ? showPassword
                  ? "text"
                  : "password"
                : type || "text"
            }
            autoComplete={"off"}
            {...(size && { size })}
            disabled={disabled}
            placeholder={placeholder ? placeholder : ""}
            value={valueEdit}
            style={{
              color: disabled
                ? "#acacac"
                : isFocused
                ? COLORS.selectedColorTheme
                : error
                ? "red"
                : COLORS.greyFontColor,
              background: COLORS.ligthGrey,
              border: disabled
                ? "2px solid #acacac"
                : isFocused
                ? "2px solid " + COLORS.selectedColorTheme
                : error
                ? "2px solid red"
                : "2px solid " + COLORS.greyFontColor,
            }}
            readOnly={disabled}
            onFocus={(e) => {
              setIsFocus(true);
              onFocus && onFocus(e);
            }}
            onBlur={(e) => {
              setIsFocus(false);
              onBlur && onBlur(e);
            }}
            onChange={(e) => {
              let newValue = e.target.value;

              if (validationPattern) {
                newValue = newValue.replace(validationPattern, "");
              }

              if (cleanWhitespaces) newValue = newValue.trim();

              setValue(newValue);
              onChange &&
                onChange({ ...e, target: { ...e.target, value: newValue } });
            }}
          />

          {!disabled && showEye && isFocused && (
            <span
              className="position-absolute top-50 end-0 translate-middle-y me-3"
              style={{ cursor: "pointer", color:COLORS.selectedColorTheme }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <Eye /> : <EyeSlash />}
            </span>
          )}

          {error && (
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip id="password-tooltip">{error}</Tooltip>}
            >
              <span className="position-absolute top-50 end-0 translate-middle-y me-3 text-danger">
                <ExclamationCircleFill style={{ cursor: "pointer" }} />
              </span>
            </OverlayTrigger>
          )}
        </div>
      </Col>
    </>
  );
};

export default InputEdit;
